import * as React from 'react';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import ArticleIcon from '@mui/icons-material/Article';
import Grid from '@mui/material/Grid';
import { useQuery } from '../hooks/useQuery';
import { useAuth } from '../hooks/useAuth';
import CircularProgress from '@mui/material/CircularProgress';
import { useNavigate, useSearchParams } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import ItemContrato from '../components/ItemContrato';
import ItemPedido from '../components/ItemPedido';
import AlertaProtocoloVencido from '../components/AlertaProtocoloVencido';
import OutlinedInput from '@mui/material/OutlinedInput';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import { encryptData, isCnpj, serializeQueryString } from '../utils';

let debounce = null;

export const ContratosPage = () => {
  const {
    listarContratos,
    detalheContrato,
    listarFornecedores,
    detalhePedido,
  } = useQuery();
  const { user, isUsuarioSebrae, logout } = useAuth();
  const [contratos, setContratos] = React.useState([]);
  const [pedidos, setPedidos] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  let [searchParams, setSearchParams] = useSearchParams();
  const [queryParams, setQueryParams] = React.useState({
    busca: '',
  });
  const [loadingAutocomplete, setLoadingAutocomplete] = React.useState(false);
  const [loadingDownload, setLoadingDownload] = React.useState(false);
  const [value, setValue] = React.useState(null);
  const [options, setOptions] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();
  const theme = useTheme();

  const filterOptions = createFilterOptions({
    matchFrom: 'any',
    stringify: (option) =>
      option.cnpj_sem_pontos + option.nome_fornec + option.cnpj_fornec,
  });

  const listar = async (cnpj) => {
    setLoading(true);
    try {
      const result = await listarContratos(
        cnpj.replace(/[^a-zA-Z0-9]/g, ''),
        serializeQueryString(queryParams, '&'),
      );

      if (result && result.data && result.data.success) {
        setContratos(result && result.data && result.data.data.contratos);
        setPedidos(result && result.data && result.data.data.pedidos);
        setLoading(false);
      } else {
        if (result.data.error == 'Accesso negado.') {
          logout();
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fornecedores = async (term) => {
    setLoadingAutocomplete(true);
    const newTerm = isCnpj(term) ? term.replace(/[^a-zA-Z0-9]/g, '') : term;

    const result = await listarFornecedores(newTerm);
    if (result && result.data && result.data.data) {
      const arr = result.data.data.map((item) => ({
        ...item,
        cnpj_sem_pontos: item.cnpj_fornec.replace(/[^a-zA-Z0-9]/g, ''),
      }));

      if (searchParams.get('cnpj')) {
        const cnpjEncontrado = arr.filter(
          ({ cnpj_sem_pontos }) =>
            searchParams.get('cnpj').replace(/[^a-zA-Z0-9]/g, '') ===
            cnpj_sem_pontos,
        );

        if (cnpjEncontrado.length) {
          setValue(cnpjEncontrado[0]);
        }
      }

      setOptions(arr);
    }
    setLoadingAutocomplete(false);
  };

  async function downloadPdf(path, filename) {
    const anchor = document.createElement('a');
    anchor.href = path;
    anchor.setAttribute('target', '_blank');
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }

  const downloadContrato = async (contrato) => {
    try {
      setLoadingDownload(true);
      const result = await detalheContrato(contrato);
      downloadPdf(result.data.data[0].contrato_pdf, 'contrato.pdf');
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingDownload(false);
    }
  };

  const downloadPedido = async (pedido) => {
    setLoadingDownload(true);
    const result = await detalhePedido(pedido);

    downloadPdf(result.data.data[0].pedido_pdf, 'pedido.pdf');
    setLoadingDownload(false);
  };

  React.useEffect(() => {
    if (!isUsuarioSebrae) {
      if (user.tipo_pessoa === 'PF') {
        listar(user.cpf);
      } else {
        listar(user.cnpj);
      }
    } else {
      if (searchParams.get('cnpj')) {
        listar(searchParams.get('cnpj'));
        fornecedores(searchParams.get('cnpj'));
        return;
      }
    }
    setLoading(false);
  }, []);

  const onChangeAutoComplete = (event) => {
    if (event.target.value.length > 0) {
      clearTimeout(debounce);
      debounce = setTimeout(() => {
        fornecedores(event.target.value);
      }, 1000);
    } else {
      setOptions([]);
    }
  };

  React.useEffect(() => {
    if (value && value.cnpj_fornec) listar(value.cnpj_fornec);
  }, [value]);

  const onSubmitSearch = (e) => {
    e.preventDefault();

    if (!isUsuarioSebrae) {
      listar(user.cnpj);
    } else {
      if (value) {
        listar(value.cnpj_sem_pontos);
      }
    }
  };

  return (
    <Box
      component="main"
      mt={8}
      sx={{ width: '100%', background: '#FAFCFF', minHeight: '100vh' }}
    >
      <AppBar position="static" color="primary" style={{ padding: 10 }}>
        <Grid container spacing={0}>
          <Grid item display="flex" alignItems="center">
            <ArticleIcon
              sx={{
                fontSize: 40,
              }}
              aria-hidden="true"
            />
            <Typography
              component="h1"
              variant="h5"
              sx={{ color: 'white', marginLeft: 1 }}
            >
              Contratos / Pedidos
            </Typography>
          </Grid>
        </Grid>
      </AppBar>

      <AlertaProtocoloVencido />

      {isUsuarioSebrae ? (
        <Box
          component="section"
          paddingX={4}
          aria-labelledby="fornecedor-search-title"
        >
          <Typography
            id="fornecedor-search-title"
            component="h2"
            variant="subtitle1"
            sx={{ color: '#0e72b8', fontWeight: 'bold', mb: 1 }}
          >
            Pesquisar fornecedor
          </Typography>
          <Autocomplete
            noOptionsText="Nenhuma opção encontrada"
            loadingText="Carregando..."
            getOptionLabel={(option) =>
              `${option.nome_fornec} - ${option.cnpj_fornec}`
            }
            options={options}
            id="controlled-demo"
            value={value}
            loading={loadingAutocomplete}
            onOpen={() => {
              setOpen(true);
            }}
            onClose={() => {
              setOpen(false);
            }}
            open={open}
            filterOptions={filterOptions}
            onChange={(event, newValue) => {
              navigate(`/dashboard/contratos?cnpj=${newValue.cnpj_sem_pontos}`);
              setValue(newValue);
            }}
            renderInput={(params) => (
              <TextField
                onChange={onChangeAutoComplete}
                {...params}
                label="Nome ou CNPJ"
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loadingAutocomplete ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
        </Box>
      ) : null}

      <Box padding={2} sx={{ maxWidth: '100%' }}>
        {!loading && (contratos.length > 0 || pedidos.length > 0) && (
          <form onSubmit={onSubmitSearch}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel htmlFor="contratos-search">
                Buscar contrato ou pedido
              </InputLabel>
              <OutlinedInput
                id="contratos-search"
                aria-label="Buscar contrato ou pedido"
                style={{ width: '100%', height: '2.4rem' }}
                type="text"
                value={queryParams.busca}
                onChange={({ target }) => {
                  setQueryParams({ busca: target.value });
                }}
                label="Buscar contrato ou pedido"
                placeholder="Procurar pelo código do contrato/pedido ou descrição..."
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      type="submit"
                      aria-label="Pesquisar contratos e pedidos"
                    >
                      <SearchOutlinedIcon />
                    </IconButton>
                  </InputAdornment>
                }
              />
            </FormControl>
          </form>
        )}
        <div className="container_table">
          <table className="app_table">
            <thead
              style={{
                textAlign: 'left',
                color: theme.palette.primary.main,
                borderTop: `8px solid ${theme.palette.primary.light}`,
                borderBottom: `1px solid #E7F0FF`,
                borderLeft: `1px solid #E7F0FF`,
                borderRight: `1px solid #E7F0FF`,
                borderRadius: '4px',
              }}
            >
              <tr>
                <th scope="col" style={{ width: '15%', padding: '1rem' }}>
                  Contrato/Pedido
                </th>
                <th scope="col" style={{ width: '17.98%', padding: '1rem' }}>
                  Descrição
                </th>
                <th scope="col" style={{ width: '20%', padding: '1rem' }}>
                  Responsável Técnico
                </th>
                <th scope="col" style={{ width: '12.66%', padding: '1rem' }}>
                  Vigência
                </th>
                <th scope="col" style={{ width: '8%', padding: '1rem' }}>
                  Tipo
                </th>
                <th scope="col" style={{ width: '24%', padding: '1rem' }}>
                  Ações
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6}>
                    <Box
                      sx={{ display: 'flex', justifyContent: 'center' }}
                      role="status"
                      aria-live="polite"
                    >
                      <CircularProgress style={{ marginTop: 60 }} />
                    </Box>
                  </td>
                </tr>
              )}

              {!loading &&
              contratos &&
              contratos.length == 0 &&
              pedidos &&
              pedidos.length == 0 ? (
                <tr>
                  <td style={{ textAlign: 'center' }} colSpan={6}>
                    Nenhum contrato/pedido.
                  </td>
                </tr>
              ) : null}

              {!loading &&
                contratos &&
                contratos.length > 0 &&
                contratos.map((contrato, index) => (
                  <ItemContrato
                    item={contrato}
                    key={index + 'contrato'}
                    index={index}
                    onClickDownload={(c) => {
                      downloadContrato(contrato.contrato_dts);
                    }}
                    onClick={() => {
                      const dataEncrypted = encryptData(
                        JSON.stringify({
                          contrato: contrato.contrato,
                          cnpj:
                            value && value.cnpj_fornec
                              ? value.cnpj_fornec
                              : user.cnpj,
                          contrato_dts: contrato.contrato_dts,
                          is_contract: true,
                          gestor: contrato.gestor_tecnico,
                        }),
                      );

                      navigate(`/dashboard/notas/${dataEncrypted}`, {});
                    }}
                  />
                ))}

              {!loading &&
                pedidos &&
                pedidos.length > 0 &&
                pedidos.map((pedido, index) => (
                  <ItemPedido
                    item={pedido}
                    index={index}
                    key={index + 'pedido'}
                    onClick={() => {
                      const dataEncrypted = encryptData(
                        JSON.stringify({
                          contrato: pedido.num_pedido,
                          cnpj:
                            value && value.cnpj_fornec
                              ? value.cnpj_fornec
                              : user.cnpj,
                          is_contract: false,
                        }),
                      );

                      navigate(`/dashboard/notas/${dataEncrypted}`);
                    }}
                  />
                ))}
            </tbody>
          </table>
        </div>
      </Box>
    </Box>
  );
};
